import React, { useState, useEffect, useContext } from "react";
import { ModalWrapper } from "../../../../components/ui";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

import handleSendMessages from "../../../../utils/api/messaging";
import { updateDispatchStage } from "../../../../services/api/dispatch";
import { captureDispatch } from "../../../../utils/business";
import { Button } from "../../../../components/ui";
import SMSCostWarning from "./SMSCostWarning";

import { IoClose } from "react-icons/io5";

import { compareWorkersByName, getWorkerSMSMessages } from "../../../../utils/business";
import { formatDate, getDateString } from "../../../../utils/datetime";
import ReviewResponseModal from "./ReviewResponseModal";

/**
 * DispatchModal component for displaying and sending SMS messages to selected workers.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Date} props.date - The date for which the SMS messages are being sent.
 * @param {Object} props.selectedWorkers - Workers to whom the SMS messages are sent.
 * @param {boolean} props.isOpen - Flag indicating whether the modal is open.
 * @param {function} props.onClose - Function to call when modal is closed (X button).
 * @returns {JSX.Element|null} The rendered component or null if the modal is not open.
 */
const DispatchModal = ({ date, selectedWorkers, isOpen, onClose }) => {
  const messages = getWorkerSMSMessages(selectedWorkers);

  const [selectedWorker, setSelectedWorker] = useState("");
  const [messageText, setMessageText] = useState("");
  const [response, setResponse] = useState(null); // Change state to store the entire response
  const [isSending, setIsSending] = useState(false);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const { weekStart } = useContext(WeeklyContext);
  const {
    dayDetails,
    setIsDispatchCapturing,
    reloadDays,
    setIsReadOnly,
    runPlanningOperation,
  } =
    useContext(DispatchContext);

  useEffect(() => {
    if (isOpen) {
      setIsSending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Update the message preview when a worker is selected in the preview dropdown
  const handleWorkerChange = (event) => {
    const workerId = event.target.value;
    setSelectedWorker(workerId);
    const selectedMessage = messages.find(
      (message) => message.worker.id === parseInt(workerId)
    );
    setMessageText(selectedMessage ? selectedMessage.message : "");
  };

  // Send all messages to the selected workers, handle the response from the API, and
  // display the response message in a new modal (ReviewResponseModal - bottom of comp)
  const handleSendMessagesClick = async () => {
    setIsSending(true);
    try {
      const response = await handleSendMessages(messages);
      setResponse(response); // Store the entire response
      setIsResponseOpen(true);
    } catch (error) {
      setResponse({
        error:
          error.response?.data?.error || "Some SMS messages failed to send",
        errors: error.response?.data?.errors || [],
        success: error.response?.data?.success || [],
      });
    }
    try {
      setIsDispatchCapturing(true);
      setIsReadOnly(false);
      captureDispatch(setIsDispatchCapturing, weekStart, date);
    } catch (error) {
      console.error(error);
    }
    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "markDayDispatched",
        resources: ["dispatch.dispatch_stage"],
        apiCall: () => updateDispatchStage("Dispatched", date),
      });
      await reloadDays();
    } catch (error) {
      console.error(error);
    }
  };

  // Close the modal and reset the states
  const handleClose = () => {
    onClose();
    setResponse(null);
    setIsResponseOpen(false);
    setMessageText("");
    setSelectedWorker("");
  };

  const dispatchDetails = dayDetails.filter(
    (detail) => detail.date === getDateString(date)
  )[0];
  const dispatchStages = dispatchDetails["dispatch_stage"];

  var alreadyDispatched = [];
  if (dispatchStages) {
    alreadyDispatched = dispatchStages.filter(
      (detail) => detail.stage === "Dispatched"
    );
  }

  return (
    <>
      {selectedWorkers && dayDetails && (
        <ModalWrapper isOpen={isOpen} onClose={handleClose} closeButton={false}>
          <div className="flex flex-row justify-center">
            <h2 className="font-bold text-lg mr-4 text-center">
              SMS Schedule for {formatDate(date, "weekday MM/DD/YY")}
            </h2>
            <div className="absolute right-6">
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => handleClose()}
              >
                <IoClose className="m-auto text-2xl" />
              </button>
            </div>
          </div>
          {alreadyDispatched.length > 0 && !isResponseOpen ? (
            <div className="text-center">
              <div className="text-red-500 rounded-xl px-2">
                Warning: SMS messages have already been sent for this day:
              </div>
              <ul className="text-red-500 list-inside inline-block text-left text-sm">
                {alreadyDispatched.map((detail, index) => {
                  const time = detail.time.split(":").slice(0, 2).join(":");
                  return (
                    <li key={index}>
                      &#8226; {detail.date}, {time} ({detail.user.first}{" "}
                      {detail.user.last})
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          <div className="flex flex-row text-md mt-2 text-red-700">
            {dayDetails.sms_author && dayDetails.sms_time ? (
              <p>
                <b className="mr-[2px]">Alert:</b> SMS scheduling messages have
                already been sent for this day by{" "}
                <b className="mr-[2px]">
                  {dayDetails.sms_author.first} {dayDetails.sms_author.last}
                </b>{" "}
                at <b className="mr-[2px]">{dayDetails.sms_time.slice(0, 5)}</b>
                .
              </p>
            ) : null}
          </div>
          <p className="mt-2 text-md text-gray-500">
            Your message will be sent to{" "}
            <b>{selectedWorkers.allWorkers.length}</b> recipients:
          </p>
          <div className="text-black grid grid-cols-2 gap-1 mt-2">
            {selectedWorkers.allWorkers
              .sort(compareWorkersByName)
              .map((worker, index) => (
                <p key={`sms-worker-${index}`} className="text-sm">
                  {worker.first} {worker.last}
                </p>
              ))}
          </div>
          <p className="mt-4 text-md text-gray-500">
            Select a worker to preview the message they will receive
          </p>
          <div className="mt-2 text-md">
            <select
              className="w-full p-2 border bg-white border-gray-300 rounded-md"
              value={selectedWorker}
              onChange={handleWorkerChange}
            >
              <option value="" disabled>
                Select a worker
              </option>
              {messages
                .sort((a, b) => compareWorkersByName(a.worker, b.worker))
                .map((message, index) => (
                  <option key={`sms-worker-${index}`} value={message.worker.id}>
                    {message.worker.first} {message.worker.last}
                  </option>
                ))}
            </select>
          </div>
          {messageText ? (
            <div>
              <p className="text-gray-500 font-bold text-md mt-4">Message</p>
              <div
                className="input-box text-sm w-auto p-2 border bg-white border-gray-300 rounded-md h-fit text-md whitespace-pre-wrap"
                onInput={(e) => setMessageText(e.currentTarget.textContent)}
              >
                {messageText}
              </div>
            </div>
          ) : null}
          <SMSCostWarning
            messageCount={selectedWorkers.allWorkers.length}
          />
          <div className="mt-4">
            <Button
              variant={isSending ? "disabled" : "secondary"}
              text={isSending ? "Sending..." : "Send All Messages"}
              onClick={handleSendMessagesClick}
              disabled={isSending}
            />
          </div>
        </ModalWrapper>
      )}
      <ReviewResponseModal
        response={response}
        isOpen={isResponseOpen}
        setIsOpen={setIsResponseOpen}
        onClose={() => handleClose()}
      />
    </>
  );
};

export default DispatchModal;
